import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { cn } from '@/lib/utils';

export const TEST_MODE_KEY = 'scanner-test-mode';
const TEST_LOG_LIMIT = 10;

export interface ScannerLogEntry {
  text: string;
  at: string;
}

export type ScannerFeedbackTone = 'success' | 'late' | 'logout' | 'error';

export interface ScannerFeedback {
  /** Drives border colour + icon overlay. */
  tone: ScannerFeedbackTone;
}

export interface BarcodeScannerProps {
  /** Called once per recognised barcode. */
  onDetected: (text: string) => void;
  /** Pause/resume the camera (e.g. while an overlay is visible). */
  paused?: boolean;
  className?: string;
  /**
   * `'default'` renders the wide rounded square historically used by the
   * full-screen kiosk page. `'compact'` and `'large'` both render the
   * mockup-style square with corner markers, a colour-coded border for
   * `feedback`, and a centred icon overlay — they differ only in clamp size:
   * compact ≤ 320 px (30vh), large ≤ 560 px (42vh).
   */
  frameVariant?: 'default' | 'compact' | 'large';
  /**
   * When set, paints the scanner frame in the matching tone and overlays a
   * check / X icon over the camera preview. The parent owns the timing — pass
   * `null` to return to the idle state.
   */
  feedback?: ScannerFeedback | null;
}

/**
 * Test-mode toggle (CLAUDE.md §22.3). Triggered from the kiosk-setup view by
 * tapping the PA logo 5 times — flag is read here on mount, refresh is enough.
 */
export function isScannerTestMode(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(TEST_MODE_KEY) === 'true';
}

/**
 * Single zxing instance, encapsulated.
 *
 * Wichtig für die iPad-Kioske: der Kamera-Stream wird EINMAL beim Mount
 * gestartet und bleibt durchgehend offen. `paused` und `onDetected` leben in
 * Refs, damit ein flippendes Overlay (Result/Error/Optimistic) NICHT zu
 * `controls.stop()` + Re-Init führt — das hat auf iPads den 1-Sekunden-
 * Flicker (Autofocus-Loop) ausgelöst.
 */
function useZxingScanner(opts: {
  videoElementId: string;
  onDetected: (text: string) => void;
  paused: boolean;
}): { detected: boolean; logs: ScannerLogEntry[] } {
  const { videoElementId, onDetected, paused } = opts;
  const [detected, setDetected] = useState(false);
  const [logs, setLogs] = useState<ScannerLogEntry[]>([]);
  const controlsRef = useRef<IScannerControls | null>(null);

  const onDetectedRef = useRef(onDetected);
  const pausedRef = useRef(paused);
  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    let cancelled = false;

    (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(undefined, videoElementId, (result) => {
          if (cancelled) return;
          if (!result) return;
          // Pause-Gating in der Callback — Stream läuft weiter, Detektionen
          // werden nur unterdrückt. So kein Restart bei Overlay-Wechsel.
          if (pausedRef.current) return;
          const text = result.getText();
          setDetected(true);
          setLogs((prev) =>
            [{ text, at: new Date().toISOString() }, ...prev].slice(0, TEST_LOG_LIMIT),
          );
          onDetectedRef.current(text);
        });
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
      } catch {
        // Camera unavailable — surface in the logs so test-mode users can
        // see it without crashing the kiosk.
        setLogs((prev) =>
          [
            {
              text: '[error: camera unavailable]',
              at: new Date().toISOString(),
            },
            ...prev,
          ].slice(0, TEST_LOG_LIMIT),
        );
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [videoElementId]);

  return { detected, logs };
}

/**
 * Scanner wrapper per CLAUDE.md §22.
 *
 *   - `@zxing/browser` is the only scanner library. The previous lazy
 *     fallback to the alternative library was removed because it produced a
 *     black preview on the school iPads — see the ADR under
 *     `docs/decisions/barcode-library.md` for the full history.
 *   - Test mode shows the live stream plus the most recent `TEST_LOG_LIMIT`
 *     codes with timestamps so a teacher can sanity-check scans during setup.
 */
export function BarcodeScanner({
  onDetected,
  paused = false,
  className,
  frameVariant = 'default',
  feedback = null,
}: BarcodeScannerProps): JSX.Element {
  const baseId = useId();
  const zxingId = `zx-video-${baseId}`;
  const [testMode, setTestMode] = useState(() => isScannerTestMode());

  // Re-read test-mode flag when the component remounts (e.g. after a refresh).
  useEffect(() => {
    setTestMode(isScannerTestMode());
  }, []);

  const handleDetected = useCallback(
    (text: string) => {
      onDetected(text);
    },
    [onDetected],
  );

  const zxing = useZxingScanner({
    videoElementId: zxingId,
    onDetected: handleDetected,
    paused,
  });

  return (
    <div
      className={cn('flex flex-col items-stretch gap-3', className)}
      data-testid="barcode-scanner"
      data-test-mode={testMode ? 'true' : 'false'}
    >
      {testMode ? (
        <ScannerPanel videoId={zxingId} logs={zxing.logs} />
      ) : frameVariant === 'compact' || frameVariant === 'large' ? (
        <KioskScannerFrame videoId={zxingId} feedback={feedback} size={frameVariant} />
      ) : (
        <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black/90">
          <video
            id={zxingId}
            className="h-full w-full object-cover"
            playsInline
            muted
            data-testid="scanner-video-zxing"
          />
        </div>
      )}
    </div>
  );
}

interface KioskScannerFrameProps {
  videoId: string;
  feedback: ScannerFeedback | null;
  size: 'compact' | 'large';
}

const KIOSK_FRAME_SIZE_CLASSES: Record<'compact' | 'large', string> = {
  // Mockup §kiosk: max 30–35 % of viewport.
  compact: 'h-[clamp(180px,30vh,320px)] w-[clamp(180px,30vh,320px)]',
  // Größeres Preview für die mittige Kiosk-Bühne (~42 vh, ≤ 560 px).
  large: 'h-[clamp(280px,42vh,560px)] w-[clamp(280px,42vh,560px)]',
};

const KIOSK_TONE_BORDER: Record<ScannerFeedbackTone, string> = {
  success: 'border-status-present text-status-present',
  late: 'border-status-late text-status-late',
  // Self-Logout: Primary-Petrol/Blau (vom Design-System), klar vom grünen
  // Anwesend-State unterscheidbar.
  logout: 'border-primary text-primary',
  error: 'border-status-absent text-status-absent',
};

const KIOSK_TONE_ICON_BG: Record<ScannerFeedbackTone, string> = {
  success: 'bg-status-present',
  late: 'bg-status-late',
  logout: 'bg-primary',
  error: 'bg-status-absent',
};

function CheckIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12.5 10 18 20 6" />
    </svg>
  );
}

function CrossIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M6 6 18 18" />
      <path d="M6 18 18 6" />
    </svg>
  );
}

/**
 * Compact kiosk-mode frame: 30vh square (≤320px) with the camera preview
 * inside, corner markers, and a colour-coded border that follows `feedback`.
 * On feedback, a circular icon (check or X) animates in centered over the
 * camera — the real "what happened" panel lives in the kiosk's middle column,
 * so this is just visual confirmation that the scan was registered.
 */
function KioskScannerFrame({ videoId, feedback, size }: KioskScannerFrameProps): JSX.Element {
  const tone = feedback?.tone ?? null;
  const borderClass = tone ? KIOSK_TONE_BORDER[tone] : 'border-border text-primary';
  const iconBg = tone ? KIOSK_TONE_ICON_BG[tone] : '';
  const showCross = tone === 'error';

  return (
    <div className="relative">
      <div
        className={cn(
          'relative grid place-items-center overflow-hidden rounded-3xl border-[3px] border-dashed bg-black/90 transition-colors duration-200',
          KIOSK_FRAME_SIZE_CLASSES[size],
          borderClass,
        )}
        data-testid="kiosk-scanner-frame"
        data-feedback-tone={tone ?? 'idle'}
        data-frame-size={size}
      >
        <video
          id={videoId}
          className="absolute inset-0 h-full w-full object-cover"
          playsInline
          muted
          data-testid="scanner-video-zxing"
        />

        {/* Corner markers — colour follows the border via `currentColor`. */}
        <CornerMarker position="tl" />
        <CornerMarker position="tr" />
        <CornerMarker position="bl" />
        <CornerMarker position="br" />

        {tone ? (
          <div
            className={cn(
              'anim-check relative grid h-[42%] w-[42%] place-items-center rounded-full text-white shadow-lg',
              iconBg,
            )}
            aria-hidden
          >
            {showCross ? (
              <CrossIcon className="h-1/2 w-1/2" />
            ) : (
              <CheckIcon className="h-1/2 w-1/2" />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CornerMarker({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }): JSX.Element {
  const base = 'absolute h-6 w-6 border-current';
  const place = {
    tl: 'top-3 left-3 border-t-[4px] border-l-[4px] rounded-tl-[12px]',
    tr: 'top-3 right-3 border-t-[4px] border-r-[4px] rounded-tr-[12px]',
    bl: 'bottom-3 left-3 border-b-[4px] border-l-[4px] rounded-bl-[12px]',
    br: 'bottom-3 right-3 border-b-[4px] border-r-[4px] rounded-br-[12px]',
  }[position];
  return <div className={cn(base, place)} aria-hidden />;
}

interface ScannerPanelProps {
  videoId: string;
  logs: ScannerLogEntry[];
}

/**
 * Test-mode panel: live video stream + scrolling log of the last
 * `TEST_LOG_LIMIT` scans with ISO timestamps. Used by the kiosk-setup view
 * to sanity-check that the camera + decoder are wired up.
 */
function ScannerPanel({ videoId, logs }: ScannerPanelProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        @zxing/browser
      </div>
      <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-black/90">
        <video
          id={videoId}
          className="h-full w-full object-cover"
          playsInline
          muted
          data-testid="scanner-video-zxing"
        />
      </div>
      <ul
        className="max-h-32 space-y-1 overflow-y-auto rounded-md border bg-card p-2 text-xs"
        aria-label="Letzte Codes"
        data-testid="scanner-log"
      >
        {logs.length === 0 ? (
          <li className="text-muted-foreground">— noch keine Codes —</li>
        ) : (
          logs.map((entry, i) => (
            <li
              key={`${entry.at}-${i}`}
              className="flex items-center justify-between gap-2 font-mono"
            >
              <span className="truncate">{entry.text}</span>
              <time className="text-muted-foreground" dateTime={entry.at}>
                {entry.at.slice(11, 19)}
              </time>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
